import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, Shield, Brain, Heart, Moon, Eye, Zap, Activity, ClipboardList, ArrowLeft, Lock, Loader, AlertTriangle } from 'lucide-react';
import { useAuth, authHeaders } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const domainLabels = {
  kaygi: 'Kaygı', ruh_hali: 'Ruh Hali', uyku: 'Uyku', dikkat: 'Odak',
  hafiza: 'Hafıza', enerji: 'Enerji', bedensel: 'Bedensel', gunluk_islev: 'İşlevsellik',
  bilissel: 'Bellek ve Düşünme', stres: 'Stres', genel_tarama: 'Genel Tarama',
};

const DomainIcon = ({ domain }) => {
  const icons = { kaygi: Shield, ruh_hali: Heart, uyku: Moon, dikkat: Eye, hafiza: Brain, enerji: Zap, bedensel: Activity, gunluk_islev: ClipboardList, bilissel: Brain };
  const Icon = icons[domain] || FileText;
  return <Icon className="w-4 h-4" />;
};

const stateLabels = {
  improving: { text: 'Toparlanma sürecinde', color: 'text-emerald-400' },
  stable: { text: 'Dengede', color: 'text-blue-400' },
  worsening: { text: 'Dikkat gerektiren dönem', color: 'text-amber-400' },
  inconsistent: { text: 'Dalgalı dönem', color: 'text-orange-400' },
};

const ReportViewPage = () => {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) return;
    loadReport();
  }, [shareToken, user]);

  const loadReport = async () => {
    try {
      const res = await fetch(`${API}/api/reports/${shareToken}`, { headers: authHeaders() });
      if (res.status === 401) {
        setError('login_required');
        setLoading(false);
        return;
      }
      if (!res.ok) throw new Error('Rapor bulunamadı');
      const data = await res.json();
      setReport(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Not logged in → show signup prompt
  if (!user) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center p-6">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-8 max-w-md text-center" data-testid="report-login-prompt">
          <Lock className="w-14 h-14 text-cyan-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-3">Raporu Görüntüle</h2>
          <p className="text-white/60 text-sm mb-6 leading-relaxed">
            Bu sağlık değerlendirme raporunu görüntülemek için Doktor Cebinde'ye üye olmanız gerekmektedir.
          </p>
          <div className="space-y-3">
            <button onClick={() => navigate(`/register?redirect=/report/${shareToken}`)}
              className="w-full py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl transition"
              data-testid="report-register-btn">
              Üye Ol
            </button>
            <button onClick={() => navigate(`/login?redirect=/report/${shareToken}`)}
              className="w-full py-3 bg-white/10 hover:bg-white/15 text-white rounded-xl transition"
              data-testid="report-login-btn">
              Giriş Yap
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center">
        <Loader className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center p-6">
        <div className="bg-white/5 border border-red-500/20 rounded-2xl p-8 max-w-md text-center">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-white/80">{error || 'Rapor bulunamadı'}</p>
          <button onClick={() => navigate('/dashboard')} className="mt-6 px-6 py-2 bg-blue-500/20 text-cyan-300 rounded-lg hover:bg-blue-500/30 transition">
            Dashboard'a Dön
          </button>
        </div>
      </div>
    );
  }

  const rd = report.report_data || {};
  const state = rd.state || {};
  const aiMsg = rd.ai_doctor_message || {};
  const testResults = rd.test_results || [];
  const cognitive = rd.cognitive_profile || {};
  const domainScores = state.domain_scores || {};
  const userState = state.user_state || 'stable';
  const sl = stateLabels[userState] || stateLabels.stable;

  return (
    <div className="min-h-screen bg-[#0f172a] py-8 px-4" data-testid="report-view-page">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-white/50 hover:text-white/80 transition">
            <ArrowLeft className="w-4 h-4" /> Geri
          </button>
          <span className="text-white/30 text-xs">{report.generated_at?.slice(0, 10)}</span>
        </div>

        {/* Title Card */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center" data-testid="report-header">
          <FileText className="w-10 h-10 text-cyan-400 mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-white mb-1">Sağlık Değerlendirme Raporu</h1>
          <p className="text-white/50 text-sm">{rd.user_name} | {report.generated_at?.slice(0, 10)}</p>
          <div className="mt-4">
            <span className={`${sl.color} text-sm font-medium`}>{sl.text}</span>
          </div>
        </div>

        {/* Doctor Commentary */}
        {aiMsg.message && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="report-doctor-comment">
            <h3 className="text-white/80 font-semibold text-sm mb-2">Doktor Yorumu</h3>
            <p className="text-white/70 text-sm leading-relaxed">{aiMsg.message}</p>
            {aiMsg.trend_story && <p className="text-white/50 text-xs mt-2">{aiMsg.trend_story}</p>}
          </div>
        )}

        {/* Domain Scores */}
        {Object.keys(domainScores).length > 0 && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="report-domain-scores">
            <h3 className="text-white/80 font-semibold text-sm mb-4">Alan Bazlı Skorlar</h3>
            <div className="space-y-3">
              {Object.entries(domainScores).sort((a, b) => b[1] - a[1]).map(([domain, score]) => {
                const color = score >= 60 ? 'bg-orange-500' : score >= 35 ? 'bg-amber-500' : 'bg-emerald-500';
                const textColor = score >= 60 ? 'text-orange-400' : score >= 35 ? 'text-amber-400' : 'text-emerald-400';
                return (
                  <div key={domain}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white/70 text-xs flex items-center gap-2">
                        <DomainIcon domain={domain} />
                        {domainLabels[domain] || domain}
                      </span>
                      <span className={`text-xs font-medium ${textColor}`}>{score.toFixed(0)}/100</span>
                    </div>
                    <div className="w-full h-1.5 bg-white/10 rounded-full">
                      <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${Math.min(score, 100)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Test Results */}
        {testResults.length > 0 && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="report-test-results">
            <h3 className="text-white/80 font-semibold text-sm mb-4">Tamamlanan Testler ({testResults.length})</h3>
            <div className="space-y-2">
              {testResults.slice(0, 15).map((tr, i) => {
                const colorMap = { emerald: 'text-emerald-400', amber: 'text-amber-400', orange: 'text-orange-400', red: 'text-red-400' };
                const tc = colorMap[tr.level_color] || 'text-white/60';
                return (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                    <div>
                      <span className="text-white/80 text-xs font-medium">{tr.test_full_name || tr.test_name}</span>
                      <span className="text-white/30 text-[10px] ml-2">{tr.completed_at?.slice(0, 10)}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-white/50 text-xs">{tr.raw_score}/{tr.max_score}</span>
                      <span className={`${tc} text-xs font-medium`}>{tr.level_label}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Cognitive Profile */}
        {cognitive.profile && cognitive.profile !== 'not_assessed' && (
          <div className="bg-teal-500/5 border border-teal-500/20 rounded-2xl p-5" data-testid="report-cognitive-profile">
            <h3 className="text-teal-300 font-semibold text-sm mb-3 flex items-center gap-2">
              <Brain className="w-4 h-4" /> Bellek ve Düşünme Değerlendirmesi
            </h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-white/60 text-xs">Bilişsel Profil</span>
                <span className={`text-xs font-medium ${cognitive.profile === 'normal' ? 'text-emerald-400' : cognitive.profile === 'follow_up' ? 'text-amber-400' : 'text-orange-400'}`}>
                  {cognitive.label}
                </span>
              </div>
              <p className="text-white/50 text-xs">{cognitive.description}</p>
              {['memory_status', 'orientation_status', 'executive_status'].map(key => {
                const labels = { memory_status: 'Hafıza', orientation_status: 'Yönelim', executive_status: 'Yürütücü İşlev' };
                const val = cognitive[key];
                return val ? (
                  <div key={key} className="flex items-center justify-between">
                    <span className="text-white/50 text-xs">{labels[key]}</span>
                    <span className={`text-xs ${val === 'normal' ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {val === 'normal' ? 'Normal' : 'Dikkat Gerektiren'}
                    </span>
                  </div>
                ) : null;
              })}
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
          <p className="text-amber-200 text-xs text-center">
            Bu rapor bilgilendirme amaçlıdır ve tıbbi tanı yerine geçmez. Herhangi bir sağlık endişesi durumunda mutlaka bir sağlık profesyoneline danışınız.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReportViewPage;
